/* __next_internal_client_entry_do_not_use__ useCardListContext,Default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Intersection } from '@splidejs/splide-extension-intersection';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/splide/css';
import { useEffect, useRef, useState, createContext, useContext } from 'react';
import { tv } from 'tailwind-variants';
import { withStandardComponentWrapper } from 'helpers/HOC';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import { getTestProps } from 'lib/testing/utils';
import { useIsMobile } from 'lib/hooks/useIsMobile';
import { useIsTablet } from 'lib/hooks/useIsTablet';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
// Create context for column count
const CardListContext = /*#__PURE__*/ createContext('1');
// Hook to use the context
export const useCardListContext = ()=>useContext(CardListContext);
const SPLIDE_INTERVAL = 3000;
const SPLIDE_SPEED = 300;
const ServiceCardCarousel = (props)=>{
    const { DynamicPlaceholderId, RenderingIdentifier, autoLoop, autoPlay } = props?.params || {};
    const boolAutoLoop = autoLoop === '1' ? true : false;
    const boolAutoPlay = autoPlay === '1' ? true : false;
    const phKey = `servicecardcarousel-${DynamicPlaceholderId}`;
    const styles = parseStyleParams(props.params, [
        'cards'
    ]);
    const colCount = styles?.cards?.cardsPerRow;
    const splideRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(boolAutoPlay);
    const isEditing = useIsEditing();
    const { base, customPagination, paginationDots, cardListBase, cardListItem } = TAILWIND_VARIANTS({
        colCount
    });
    useEffect(()=>{
        if (!splideRef.current) return;
        if (boolAutoPlay) {
            splideRef.current.Components.Autoplay.play();
        } else {
            splideRef.current.Components.Autoplay.pause();
        }
        setIsPlaying(boolAutoPlay);
    }, [
        boolAutoPlay,
        splideRef,
        boolAutoLoop
    ]);
    const splideOptions = {
        autoplay: isPlaying,
        arrows: false,
        intersection: {
            inView: {
                autoplay: isPlaying
            },
            outView: {
                autoplay: false
            }
        },
        interval: SPLIDE_INTERVAL,
        pagination: true,
        perMove: 1,
        perPage: 1,
        rewind: boolAutoLoop || isPlaying,
        speed: SPLIDE_SPEED,
        type: 'slide',
        width: '100%',
        drag: false,
        rewindByDrag: boolAutoLoop || isPlaying,
        classes: {
            pagination: `${customPagination()}`,
            page: `${paginationDots()}`
        },
        breakpoints: {
            1024: {
                drag: true,
                perMove: 2,
                perPage: 2
            },
            767: {
                perMove: 1,
                perPage: 1
            }
        }
    };
    /*
   * Rendering
   */ const isTablet = useIsTablet();
    const isMobile = useIsMobile();
    if (isEditing) {
        return /*#__PURE__*/ _jsxs("section", {
            "data-component": "authorable/shared/lists/servicecardcarousel",
            ...editingHydrationProps(isEditing),
            id: RenderingIdentifier,
            children: [
                /*#__PURE__*/ _jsx(EditingHelpText, {
                    children: "Carousel rendered as flat list for ease of editing. Use preview to see carousel in action."
                }),
                /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                    name: phKey,
                    rendering: props.rendering,
                    // Wrap the component in a <Splide>.
                    // If there are no components it won't render an empty carousel
                    // It also ensures that when editing in Page Builder, it will show the help text outside of the <ul>
                    render: (components)=>/*#__PURE__*/ _jsx("div", {
                            className: base(),
                            children: components.map((comp, index)=>/*#__PURE__*/ _jsx("div", {
                                    className: "relative",
                                    children: comp
                                }, index))
                        })
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx("section", {
        "data-component": "authorable/shared/lists/servicecardcarousel",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-servicecardcarousel-${props?.rendering?.uid}`),
        className: "w-full",
        children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
            name: phKey,
            rendering: props.rendering,
            render: (components)=>{
                if (!(isTablet || isMobile)) {
                    return /*#__PURE__*/ _jsx("ul", {
                        className: cardListBase(),
                        "aria-label": "List of cards",
                        children: components.map((comp, index)=>/*#__PURE__*/ _jsx("li", {
                                className: cardListItem(),
                                role: "listitem",
                                ...getTestProps(`card-${index}`),
                                children: comp
                            }, index))
                    });
                }
                const isTabletOnly = isTablet && !isMobile;
                const shouldAddBlankSlide = isTabletOnly && components.length % 2 === 1;
                const slides = shouldAddBlankSlide ? [
                    ...components,
                    /*#__PURE__*/ _jsx("div", {
                        "aria-hidden": "true"
                    }, "blank")
                ] : components;
                return /*#__PURE__*/ _jsx("div", {
                    className: base(),
                    children: /*#__PURE__*/ _jsx(Splide, {
                        ref: (ref)=>{
                            if (ref?.splide) {
                                splideRef.current = ref.splide;
                            }
                        },
                        extensions: {
                            Intersection
                        },
                        hasTrack: false,
                        options: splideOptions,
                        children: /*#__PURE__*/ _jsx(SplideTrack, {
                            className: "-mx-spacing-spacing-3",
                            children: slides.map((comp, index)=>{
                                const isPlaceholder = index >= components.length;
                                return /*#__PURE__*/ _jsx(SplideSlide, {
                                    tabIndex: 0,
                                    "aria-hidden": false,
                                    ...getTestProps(isPlaceholder ? `slide-blank` : `slide-${index}`),
                                    className: "py-spacing-spacing-8 px-spacing-spacing-12",
                                    children: comp ?? /*#__PURE__*/ _jsx("div", {
                                        "aria-hidden": "true"
                                    })
                                }, index);
                            })
                        })
                    })
                });
            }
        })
    });
};
export const Default = withStandardComponentWrapper(ServiceCardCarousel, false);
const TAILWIND_VARIANTS = tv({
    defaultVariants: {
        colCount: '1'
    },
    slots: {
        base: [
            'overflow-hidden',
            'relative',
            'flex',
            'flex-col',
            'md:gap-0',
            'gap-spacing-spacing-16'
        ],
        customPagination: [
            'pt-component-section-container-gutter',
            'flex',
            'justify-center',
            '[&>li]:p-[6px]'
        ],
        paginationDots: [
            'w-3',
            'h-3',
            'border',
            'border-color-border-dark',
            'rounded-full',
            '[&.is-active]:w-[40px]',
            '[&.is-active]:bg-color-fill-dark'
        ],
        cardListBase: [
            'gap-x-layout-base-gutter',
            'gap-y-spacing-spacing-24',
            'grid',
            'grid-cols-2'
        ],
        cardListItem: [
            'flex',
            'flex-col'
        ]
    },
    variants: {
        colCount: {
            '1': {
                base: [
                    'lg:grid-cols-1'
                ]
            },
            '2': {
                base: [
                    'lg:grid-cols-2'
                ]
            }
        }
    }
});
